/**
 * Location step component
 * Step 2 of job wizard - area selection
 * City search on the left, drawable map on the right
 * Supports both rectangle and freehand polygon selection
 */

import { useState, useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Row, Col, Typography, Card, Space, Alert, Button, Divider } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { CitySearch } from './CitySearch';
import { MapContainer } from '@/components/maps/MapContainer';
import { DrawControls, type DrawMode } from '@/components/maps/DrawControls';
import { GridOverlay } from '@/components/maps/GridOverlay';
import type { CreateJobInput } from '@/types/job';
import type { Bounds, City, GridConfig, PolygonCoordinates, SelectionType } from '@/types/grid';
import type { Map as LeafletMap } from 'leaflet';
import { calculateArea, generateGridPreview, zoomToCellSize } from '@/utils/mapUtils';
import { formatNumber } from '@/utils/formatters';
import { MAP_DEFAULTS } from '@/utils/constants';

const { Title, Text, Paragraph } = Typography;

// Country presets with simplified border polygons
interface CountryPreset {
  name: string;
  flag: string;
  bounds: Bounds;
  polygon: PolygonCoordinates;
}

const COUNTRY_PRESETS: CountryPreset[] = [
  {
    name: 'Germany',
    flag: '🇩🇪',
    bounds: {
      north: 55.1,
      south: 47.2,
      east: 15.1,
      west: 5.8,
    },
    // Simplified Germany border polygon (main territory)
    polygon: [
      { lat: 54.8, lng: 9.4 },   // Northern tip (Flensburg area)
      { lat: 54.5, lng: 13.4 },  // NE coast (Rügen)
      { lat: 53.9, lng: 14.2 },  // Usedom
      { lat: 51.3, lng: 14.9 },  // Eastern border (Görlitz)
      { lat: 50.9, lng: 14.8 },  // Saxon Switzerland
      { lat: 50.2, lng: 12.1 },  // Czech border
      { lat: 48.5, lng: 13.8 },  // SE corner (Passau area)
      { lat: 47.5, lng: 13.0 },  // Alps (Berchtesgaden)
      { lat: 47.3, lng: 10.2 },  // Southern tip (Allgäu)
      { lat: 47.6, lng: 8.6 },   // Lake Constance
      { lat: 47.6, lng: 7.5 },   // SW corner (Basel area)
      { lat: 49.0, lng: 8.2 },   // Upper Rhine
      { lat: 49.5, lng: 6.4 },   // Saarland
      { lat: 50.0, lng: 6.1 },   // Western border
      { lat: 51.0, lng: 5.9 },   // Aachen area
      { lat: 51.8, lng: 6.8 },   // Lower Rhine
      { lat: 52.5, lng: 6.7 },   // NW border
      { lat: 53.5, lng: 7.2 },   // East Frisia
      { lat: 53.9, lng: 8.5 },   // North Sea coast
      { lat: 54.3, lng: 8.6 },   // Schleswig
      { lat: 54.8, lng: 9.4 },   // Back to start
    ],
  },
  {
    name: 'Austria',
    flag: '🇦🇹',
    bounds: {
      north: 49.1,
      south: 46.3,
      east: 17.2,
      west: 9.5,
    },
    // Simplified Austria border polygon
    polygon: [
      { lat: 48.8, lng: 13.8 },  // NE corner (Gmünd area)
      { lat: 48.6, lng: 15.0 },  // Weinviertel
      { lat: 48.1, lng: 16.9 },  // Vienna / Bratislava border
      { lat: 47.7, lng: 16.4 },  // Burgenland east
      { lat: 47.1, lng: 16.5 },  // SE Burgenland
      { lat: 46.6, lng: 16.0 },  // Slovenia border
      { lat: 46.7, lng: 15.0 },  // Carinthia south
      { lat: 46.5, lng: 13.7 },  // Carinthia / Italy border
      { lat: 46.6, lng: 12.4 },  // East Tyrol
      { lat: 47.0, lng: 11.8 },  // Brenner area
      { lat: 47.0, lng: 10.5 },  // Tyrol south
      { lat: 46.8, lng: 10.1 },  // Reschenpass area
      { lat: 47.1, lng: 9.6 },   // Vorarlberg south (Montafon)
      { lat: 47.5, lng: 9.6 },   // Bregenz / Lake Constance
      { lat: 47.6, lng: 9.9 },   // Lindau border
      { lat: 47.5, lng: 10.3 },  // Allgäu border
      { lat: 47.4, lng: 10.9 },  // Zugspitze area
      { lat: 47.3, lng: 11.4 },  // Inntal
      { lat: 47.4, lng: 12.2 },  // Kitzbühel area
      { lat: 47.7, lng: 12.9 },  // Salzburg area
      { lat: 47.7, lng: 13.1 },  // Salzburg north
      { lat: 48.0, lng: 13.4 },  // Upper Austria (Inn border)
      { lat: 48.5, lng: 13.7 },  // Passau area
      { lat: 48.8, lng: 13.8 },  // Back to start
    ],
  },
  {
    name: 'Switzerland',
    flag: '🇨🇭',
    bounds: {
      north: 47.9,
      south: 45.8,
      east: 10.5,
      west: 5.9,
    },
    // Simplified Switzerland border polygon
    polygon: [
      { lat: 47.8, lng: 8.6 },   // Northern tip (Schaffhausen)
      { lat: 47.6, lng: 9.5 },   // Lake Constance / St. Gallen
      { lat: 47.3, lng: 9.6 },   // Appenzell / Rhine valley
      { lat: 47.1, lng: 9.5 },   // Liechtenstein border
      { lat: 46.9, lng: 10.5 },  // Engadin east (Müstair)
      { lat: 46.5, lng: 10.1 },  // Ofenpass area
      { lat: 46.2, lng: 9.0 },   // Ticino east (Chiasso area)
      { lat: 45.9, lng: 8.7 },   // Southern tip (Ticino / Como)
      { lat: 46.1, lng: 8.0 },   // Lago Maggiore
      { lat: 46.0, lng: 7.0 },   // Valais south (Zermatt area)
      { lat: 45.9, lng: 6.8 },   // Mont Blanc area
      { lat: 46.2, lng: 6.1 },   // Geneva south
      { lat: 46.5, lng: 6.1 },   // Geneva lake west
      { lat: 46.8, lng: 6.2 },   // Jura south
      { lat: 47.3, lng: 6.9 },   // Jura north (Delémont)
      { lat: 47.5, lng: 7.5 },   // Basel area
      { lat: 47.6, lng: 7.6 },   // Basel north
      { lat: 47.6, lng: 8.2 },   // Aargau
      { lat: 47.7, lng: 8.7 },   // Zurich north
      { lat: 47.8, lng: 8.6 },   // Back to start
    ],
  },
];

export function LocationStep() {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CreateJobInput>();

  const [mapRef, setMapRef] = useState<LeafletMap | null>(null);
  const [drawMode, setDrawMode] = useState<DrawMode>('view');

  const bounds = watch('bounds');
  const polygon = watch('polygon');
  const selectionType = watch('selectionType');
  const zoomLevel = watch('zoomLevel') || 16;

  const gridConfig: GridConfig = useMemo(() => ({
    zoomLevel,
    cellSizeKm: zoomToCellSize(zoomLevel),
    overlapPercent: 10,
  }), [zoomLevel]);

  const gridPreview = useMemo(() => {
    if (!bounds) return null;
    return generateGridPreview(bounds, gridConfig, polygon);
  }, [bounds, gridConfig, polygon]);

  const areaInfo = useMemo(() => {
    if (!bounds) return null;
    const areaKm2 = calculateArea(bounds);
    return {
      area: areaKm2,
      cells: gridPreview?.totalCells || 0,
      estimatedTime: gridPreview ? Math.ceil((gridPreview.totalCells * 3) / 60) : 0,
    };
  }, [bounds, gridPreview]);

  const handleCitySelect = useCallback((city: City) => {
    // Store location name for reference but don't auto-set bounds
    // Let the user draw their own selection area
    setValue('locationName', city.name, { shouldValidate: true });

    // Fly to the selected city on the map so user can draw their area
    if (mapRef && city.bounds) {
      mapRef.fitBounds([
        [city.bounds.south, city.bounds.west],
        [city.bounds.north, city.bounds.east],
      ], { padding: [20, 20] });
    }
  }, [setValue, mapRef]);

  const handleBoundsChange = useCallback((newBounds: Bounds | undefined) => {
    setValue('bounds', newBounds, { shouldValidate: true });
    setValue('locationName', undefined); // Clear location name when manually drawing
  }, [setValue]);

  const handlePolygonChange = useCallback((newPolygon: PolygonCoordinates | undefined) => {
    setValue('polygon', newPolygon);
  }, [setValue]);

  const handleSelectionTypeChange = useCallback((type: SelectionType) => {
    setValue('selectionType', type);
    // Clear polygon when switching to rectangle mode
    if (type === 'rectangle') {
      setValue('polygon', undefined);
    }
  }, [setValue]);

  const handleMapReady = useCallback((map: LeafletMap) => {
    setMapRef(map);
  }, []);

  const handlePresetSelect = useCallback((preset: CountryPreset) => {
    // Set the polygon and bounds
    setValue('polygon', preset.polygon);
    setValue('bounds', preset.bounds, { shouldValidate: true });
    setValue('selectionType', 'polygon');
    setValue('locationName', preset.name, { shouldValidate: true });

    // Fly to the area on the map
    if (mapRef) {
      mapRef.fitBounds([
        [preset.bounds.south, preset.bounds.west],
        [preset.bounds.north, preset.bounds.east],
      ], { padding: [20, 20] });
    }

    // Switch to view mode to show the selection
    setDrawMode('view');
  }, [setValue, mapRef]);

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        Define the search area
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Search for a city, draw a rectangle, or use freehand to trace a custom shape.
      </Paragraph>

      <Row gutter={24}>
        {/* Left side - City search and presets */}
        <Col xs={24} lg={10}>
          <Card style={{ height: '100%' }}>
            <CitySearch onSelect={handleCitySelect} />

            {/* Country presets */}
            <Divider style={{ margin: '16px 0 12px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <GlobalOutlined /> Country Presets
              </Text>
            </Divider>
            <Space wrap>
              {COUNTRY_PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset)}
                  icon={<span style={{ marginRight: 4 }}>{preset.flag}</span>}
                >
                  {preset.name}
                </Button>
              ))}
            </Space>

            {errors.bounds && (
              <Alert
                type="error"
                message={errors.bounds.message || 'Please select a valid area'}
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>

        {/* Right side - Drawable map */}
        <Col xs={24} lg={14}>
          <Card
            style={{ height: '100%' }}
            styles={{ body: { padding: 12 } }}
          >
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
              <MapContainer
                height={350}
                center={MAP_DEFAULTS.center}
                zoom={MAP_DEFAULTS.initialZoom}
                onMapReady={handleMapReady}
              >
                <DrawControls
                  initialBounds={bounds}
                  initialPolygon={polygon}
                  onBoundsChange={handleBoundsChange}
                  onPolygonChange={handlePolygonChange}
                  onSelectionTypeChange={handleSelectionTypeChange}
                  mode={drawMode}
                  onModeChange={setDrawMode}
                />

                {/* Show grid preview when bounds exist and in view mode */}
                {bounds && drawMode === 'view' && gridPreview && (
                  <GridOverlay bounds={bounds} gridPreview={gridPreview} polygon={polygon} />
                )}
              </MapContainer>
            </div>

            {/* Area info below map */}
            {areaInfo ? (
              <div style={{ marginTop: 12 }}>
                <Space size={24} style={{ width: '100%', justifyContent: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Area</Text>
                    <Text strong>~{areaInfo.area.toFixed(1)} km²</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Grid cells</Text>
                    <Text strong>{formatNumber(areaInfo.cells)}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Est. time</Text>
                    <Text strong>~{areaInfo.estimatedTime} min</Text>
                  </div>
                </Space>

                {areaInfo.cells > 500 && (
                  <Alert
                    type="warning"
                    message="Large area - this may take a while"
                    style={{ marginTop: 12 }}
                    showIcon
                  />
                )}
              </div>
            ) : (
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <Text type="secondary">
                  Select a city, draw a rectangle, or freehand an area on the map
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default LocationStep;
